import { ValidationLane } from '@atlas/types'
import type { StepFooterCta } from '@/components/work-orders/WorkOrderStepFooter'
import { hasUndoneChangeRequestItem } from '@/domain/change-requests'
import type { WorkOrder } from '@/domain/work-order.types'
import type { ChangeRequest, ResolvedWorkflowSnapshot, WorkflowTransitionSnapshot } from '@/domain/workflow.types'
import { evaluateTransitionGuards } from '@/domain/workflow-stub'
import { useTransitionWorkOrder } from '@/hooks/useWorkOrders'
import { apiErrorMessage } from '@/lib/api-error'
import { notification } from '@/notifications/notification-store'
import { DistributionPackageChoice } from './SourceMaterialsSection'
import { CREATED_STATUS, hasBuiltScreen, StageTurn, turnOf } from './stage-turn'
import { SourceMaterialVerdict } from './VerifySourceMaterials'

/** Where the PM's move goes once they confirm a created order's request is complete. */
const CONFIRM_TARGET = 'backlog'

/** Where the PM's move goes when there is no distribution package for the designer to review. */
const NO_PACKAGE_TARGET = 'sources_verified'

/** Where the PM's move goes when there is a package to review. */
const PM_TARGET = 'review_source_material'

/** Where Submit sends the order for each verdict the designer can pick. */
const VERDICT_TARGET: Readonly<Record<SourceMaterialVerdict, string>> = {
	[SourceMaterialVerdict.READY]: 'sources_verified',
	[SourceMaterialVerdict.ISSUE]: 'pending_source_material',
}

const ARRIVAL_TOAST: Readonly<Record<string, string>> = {
	[CONFIRM_TARGET]: 'Request confirmed',
	review_source_material: 'Task sent to designer',
	sources_verified: 'Source material verified',
	pending_source_material: 'Task sent back to PM',
}

/** What the people on the screen have picked, which decides where the footer action goes. */
export interface StageChoices {
	/** The designer's verdict on the package. */
	verdict: SourceMaterialVerdict | null
	/** The PM's answer to whether there is a package at all. */
	packageChoice: DistributionPackageChoice | null
	/** Whether the order carries any package material: a Veeva code or link, or an uploaded file. */
	hasPackage: boolean
	/** The order's source-material change requests, which the designer's moves are guarded on. */
	changeRequests: readonly ChangeRequest[]
}

/** The status the action targets, and why it cannot be taken yet. */
function targetOf(status: string, { verdict, packageChoice, hasPackage, changeRequests }: StageChoices) {
	// The `fields_present` guard on the move is what holds it back while the request is incomplete.
	if (status === CREATED_STATUS) return { target: CONFIRM_TARGET, isBlocked: false }
	if (turnOf(status) === StageTurn.DESIGNER) {
		// Before a verdict, Submit still shows (disabled) under the label both its moves share.
		return { target: VERDICT_TARGET[verdict ?? SourceMaterialVerdict.READY], isBlocked: !verdict }
	}
	// Every item the designer sent back must be ticked done before the PM moves on.
	const hasUndone = hasUndoneChangeRequestItem(changeRequests, status)
	if (packageChoice === DistributionPackageChoice.NONE) return { target: NO_PACKAGE_TARGET, isBlocked: hasUndone }
	// Before a choice, the move to the designer shows (disabled); it also needs something to review.
	return { target: PM_TARGET, isBlocked: !packageChoice || !hasPackage || hasUndone }
}

/** One move as a pressable action, reporting its outcome through the notification store. */
function moveAction(
	transition: ReturnType<typeof useTransitionWorkOrder>,
	workOrder: WorkOrder,
	move: WorkflowTransitionSnapshot,
	arrivalLabel: string,
	successToast: string,
	isDisabled: boolean,
): StepFooterCta {
	return {
		label: move.ctaLabel,
		isDisabled,
		isPending: transition.isPending,
		onPress: () =>
			transition.mutate(
				{ id: workOrder.workOrderId, toStatus: move.toStatusId },
				{
					onSuccess: () => notification.success(successToast),
					onError: (error) =>
						notification.error(apiErrorMessage(error, `Could not move ${workOrder.orderCode} to ${arrivalLabel}.`)),
				},
			),
	}
}

/** The snapshot, while it still describes the order's current status. */
const currentSnapshot = (workOrder: WorkOrder, snapshot: ResolvedWorkflowSnapshot | null | undefined) =>
	snapshot && snapshot.currentStatusId === workOrder.status ? snapshot : null

const labelOf = (snapshot: ResolvedWorkflowSnapshot, code: string) =>
	snapshot.statuses.find((s) => s.code === code)?.label ?? code

/**
 * The footer action for the signed-in user on a step with its own screen, or null when they have no move.
 *
 * The label is the configured transition's `ctaLabel`, and the transition must be one the
 * snapshot lists as legal: if an administrator removed it, the action disappears rather
 * than being guessed. The snapshot is only trusted while it describes the order's current
 * status, so the action is hidden in the moment between an optimistic move and its refetch.
 */
function useStageCta(
	workOrder: WorkOrder,
	snapshot: ResolvedWorkflowSnapshot | null | undefined,
	choices: StageChoices,
	holdsTurn: boolean,
): StepFooterCta | null {
	const transition = useTransitionWorkOrder()
	const current = currentSnapshot(workOrder, snapshot)
	if (!holdsTurn || !current) return null

	const { target, isBlocked } = targetOf(workOrder.status, choices)
	const move = current.legalTransitions.find((t) => t.toStatusId === target)
	if (!move) return null

	const arrivalLabel = labelOf(current, target)
	const skipsReview =
		choices.packageChoice === DistributionPackageChoice.NONE && turnOf(workOrder.status) === StageTurn.PM
	const successToast = skipsReview
		? 'Source material review skipped'
		: (ARRIVAL_TOAST[target] ?? `Moved to ${arrivalLabel}`)
	const isGuarded = !evaluateTransitionGuards(move.guards, workOrder, {
		changeRequests: { [ValidationLane.SOURCE_MATERIAL]: [...choices.changeRequests] },
	}).ok
	return moveAction(transition, workOrder, move, arrivalLabel, successToast, isBlocked || isGuarded)
}

/**
 * Every legal move for the turn holder on a step without its own screen, in the
 * workflow's configured order: the first is the footer's action, the rest sit on the page.
 */
function useStepMoves(
	workOrder: WorkOrder,
	snapshot: ResolvedWorkflowSnapshot | null | undefined,
	holdsTurn: boolean,
): StepFooterCta[] {
	const transition = useTransitionWorkOrder()
	const current = currentSnapshot(workOrder, snapshot)
	if (!holdsTurn || !current) return []
	return current.legalTransitions.map((move) => {
		const arrivalLabel = labelOf(current, move.toStatusId)
		const isGuarded = !evaluateTransitionGuards(move.guards, workOrder, {}).ok
		return moveAction(transition, workOrder, move, arrivalLabel, `Moved to ${arrivalLabel}`, isGuarded)
	})
}

/** The footer action, and on a step without its own screen the other moves the page offers. */
export function useStepActions(
	workOrder: WorkOrder,
	snapshot: ResolvedWorkflowSnapshot | null | undefined,
	choices: StageChoices,
	holdsTurn: boolean,
): { cta: StepFooterCta | null; otherMoves: StepFooterCta[] } {
	const stageCta = useStageCta(workOrder, snapshot, choices, holdsTurn)
	const [stepCta = null, ...otherMoves] = useStepMoves(workOrder, snapshot, holdsTurn)
	return hasBuiltScreen(workOrder.status) ? { cta: stageCta, otherMoves: [] } : { cta: stepCta, otherMoves }
}
